import type { StateDetailTerritory } from '../../types';

interface TerritoryHoverCardProps {
  position: { x: number; y: number };
  territory: StateDetailTerritory;
}

function formatPrefixRange(prefixes: string[]): string {
  if (prefixes.length === 0) return '';
  if (prefixes.length <= 3) return prefixes.map(p => `${p}xx`).join(', ');

  // Find contiguous ranges
  const sorted = [...prefixes].sort();
  const ranges: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i];
    if (parseInt(curr) === parseInt(prev) + 1) {
      prev = curr;
    } else {
      ranges.push(start === prev ? `${start}xx` : `${start}xx\u2013${prev}xx`);
      start = curr;
      prev = curr;
    }
  }
  ranges.push(start === prev ? `${start}xx` : `${start}xx\u2013${prev}xx`);

  // Truncate if too many ranges
  if (ranges.length > 3) {
    return `${ranges.slice(0, 3).join(', ')} +${ranges.length - 3} more`;
  }
  return ranges.join(', ');
}

export default function TerritoryHoverCard({ position, territory }: TerritoryHoverCardProps) {
  return (
    <div
      className="absolute z-50 pointer-events-none"
      style={{ left: position.x + 12, top: position.y - 10 }}
    >
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 min-w-[180px] max-w-[260px]">
        <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{territory.name}</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {territory.repName ?? 'OPEN'}
        </div>

        {territory.zipPrefixes.length > 0 && (
          <div className="text-xs text-gray-400 dark:text-gray-500 mt-1 font-mono">
            {formatPrefixRange(territory.zipPrefixes)}
          </div>
        )}

        <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            <span className="text-xs text-gray-600 dark:text-gray-400">{territory.blastCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-red-500" />
            <span className="text-xs text-gray-600 dark:text-gray-400">{territory.competitorCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-gray-400" />
            <span className="text-xs text-gray-600 dark:text-gray-400">{territory.openCount}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
